import { useController } from "react-hook-form"

import {
	Checkbox,
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuSeparator,
	FormSwitch,
	SelectTrigger,
	Tooltip,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { getProjectIcon } from "@/lib/projects"
import { SettingItem } from "../settings-switches/setting-item"

type Team = NonNullable<RouterOutputs["linear"]["teams"]>[number]

const CHECKBOX_CLASS =
	"data-[state=checked]:border-gray-900 data-[state=checked]:bg-gray-900 data-[state=checked]:hover:bg-gray-900 dark:data-[state=checked]:border-white dark:data-[state=checked]:bg-white dark:data-[state=checked]:text-gray-900"

function TeamIcon({ team }: { team: Team }) {
	const Icon = getProjectIcon(team.icon)
	return (
		<div
			className="flex h-6 w-6 flex-none items-center justify-center rounded-md"
			style={{ backgroundColor: `${team.color}15` }}
		>
			<Icon style={{ color: team.color }} />
		</div>
	)
}

/** Which Linear teams show in Productlane, and which ones it syncs. */
export function TeamTable({
	teams,
	noneSynced,
}: {
	teams: Team[]
	noneSynced: boolean
}) {
	return (
		<>
			<div className="flex flex-col gap-3">
				<div>
					<div className="text-sm font-medium text-primary">Visible teams</div>
					<div className="text-xs font-normal tracking-tight text-secondary">
						Select which teams are visible in Productlane.{" "}
						<Tooltip
							label='Productlane sees only the Linear teams you picked when connecting. Reconnect and choose "All teams" to bring in the rest.'
							contentClassName="max-w-64"
						>
							<button
								type="button"
								className="underline decoration-gray-400 decoration-dotted underline-offset-2 dark:decoration-gray-500"
							>
								Missing a team?
							</button>
						</Tooltip>
					</div>
				</div>
				<div className="space-y-4">
					{teams.map((team) => (
						<div className="flex w-full items-center gap-3" key={team.id}>
							<TeamIcon team={team} />
							<span className="truncate text-sm font-medium text-primary">
								{team.name}
							</span>
							<span className="flex-none text-sm text-tertiary">
								{team.key}
							</span>
							<div className="ml-auto flex-none">
								<FormSwitch name={`selectedTeamIds.${team.id}`} />
							</div>
						</div>
					))}
				</div>
			</div>
			<SettingItem
				title="Synced teams"
				description="Teams Productlane imports customer requests from."
				action={<SyncedTeamsSelect teams={teams} allTeams={noneSynced} />}
				className="pt-4"
			/>
		</>
	)
}

/** Turning one team off from "All teams" keeps every other team on. */
export function toggleSyncedTeam(
	teams: Pick<Team, "id">[],
	synced: Record<string, boolean>,
	allTeams: boolean,
	teamId: string,
): Record<string, boolean> {
	if (allTeams) {
		return Object.fromEntries(teams.map(({ id }) => [id, id !== teamId]))
	}
	return { ...synced, [teamId]: !synced[teamId] }
}

/** What the trigger reads for the current choice. */
export function syncedTeamsLabel(
	chosen: Pick<Team, "name">[],
	allTeams: boolean,
): string {
	if (allTeams) return "All teams"
	if (chosen.length === 1) return chosen[0]!.name
	return `${chosen.length} teams`
}

// No team switched on means every team syncs, including ones created later.
function SyncedTeamsSelect({
	teams,
	allTeams,
}: {
	teams: Team[]
	allTeams: boolean
}) {
	const { field } = useController({ name: "linearSyncTeamIds" })
	const synced = (field.value ?? {}) as Record<string, boolean>
	const chosen = teams.filter((team) => synced[team.id])

	const write = (next: Record<string, boolean>) => field.onChange(next)

	const toggle = (teamId: string) =>
		write(toggleSyncedTeam(teams, synced, allTeams, teamId))

	const label = syncedTeamsLabel(chosen, allTeams)

	return (
		<DropdownMenu
			trigger={
				<SelectTrigger className="w-40 dark:bg-gray-700/70">
					{label}
				</SelectTrigger>
			}
			contentProps={{ align: "end" }}
			className="max-h-80 min-w-64 overflow-y-auto"
		>
			<DropdownMenuItem
				onSelect={(event) => {
					event.preventDefault()
					if (!allTeams) write({})
				}}
				className="items-start gap-3"
			>
				<Checkbox
					size="sm"
					checked={allTeams}
					tabIndex={-1}
					className="pointer-events-none mt-0.5"
					indicatorClassName={CHECKBOX_CLASS}
				/>
				<div>
					<div>All teams</div>
					<div className="text-xs font-normal text-tertiary">
						Includes teams added later
					</div>
				</div>
			</DropdownMenuItem>
			<DropdownMenuSeparator />
			{teams.map((team) => {
				const checked = allTeams || !!synced[team.id]
				// The last chosen team stays on, since none chosen means all teams.
				const isLastChosen = !allTeams && chosen.length === 1 && checked
				return (
					<DropdownMenuItem
						key={team.id}
						disabled={isLastChosen}
						onSelect={(event) => {
							event.preventDefault()
							toggle(team.id)
						}}
						className="gap-3"
					>
						<Checkbox
							size="sm"
							checked={checked}
							tabIndex={-1}
							className="pointer-events-none"
							indicatorClassName={CHECKBOX_CLASS}
						/>
						<TeamIcon team={team} />
						<span className="truncate">{team.name}</span>
					</DropdownMenuItem>
				)
			})}
		</DropdownMenu>
	)
}
